import { Box, Image, Stack, Text } from '@mantine/core'
import { useElementSize } from '@mantine/hooks'

import {
  MIN_SUPPORTED_SCREEN_HEIGHT,
  MIN_SUPPORTED_SCREEN_WIDTH,
  MOBILE_SPLASH_FONT_FAMILY,
  MOBILE_SPLASH_GAP,
  MOBILE_SPLASH_IMAGE_SIZE,
  MOBILE_SPLASH_LINE_HEIGHT,
  MOBILE_SPLASH_MAX_LINES,
  MOBILE_SPLASH_OUTER_PADDING_X,
  MOBILE_SPLASH_SUBTITLE_FONT_SIZE,
  MOBILE_SPLASH_SUBTITLE_LETTER_SPACING,
  MOBILE_SPLASH_TEXT_PADDING_X,
  MOBILE_SPLASH_TITLE_FONT_SIZE,
  MOBILE_SPLASH_TITLE_LETTER_SPACING,
  SPLASH_PREVIEW_MAX_WIDTH,
} from './brandingMobileLayout'

interface BrandSplashPreviewProps {
  brandTitle: string
  brandSubtitle: string
  brandColor: string
  imagePreviewUrl: string | null
}

/**
 * Renders the mobile splash preview at true iPhone-SE pixels (375x667) and
 * CSS-scales to fit the rendered frame width, so wrap behavior in the preview
 * matches what the mobile app will actually do.
 */
export function BrandSplashPreview({
  brandTitle,
  brandSubtitle,
  brandColor,
  imagePreviewUrl,
}: BrandSplashPreviewProps) {
  const { ref: frameRef, width: frameWidth } = useElementSize()

  // Fall back to the max-width ratio on first paint (frame may be in an inactive tab).
  const scale =
    frameWidth > 0 ? frameWidth / MIN_SUPPORTED_SCREEN_WIDTH : SPLASH_PREVIEW_MAX_WIDTH / MIN_SUPPORTED_SCREEN_WIDTH

  const lightBg = isLightColor(brandColor)

  return (
    <Box
      ref={frameRef}
      style={{
        border: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 24,
        overflow: 'hidden',
        width: '100%',
        maxWidth: SPLASH_PREVIEW_MAX_WIDTH,
        aspectRatio: `${MIN_SUPPORTED_SCREEN_WIDTH} / ${MIN_SUPPORTED_SCREEN_HEIGHT}`,
        backgroundColor: brandColor,
        position: 'relative',
      }}>
      <Box
        style={{
          width: MIN_SUPPORTED_SCREEN_WIDTH,
          height: MIN_SUPPORTED_SCREEN_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          paddingLeft: MOBILE_SPLASH_OUTER_PADDING_X,
          paddingRight: MOBILE_SPLASH_OUTER_PADDING_X,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: MOBILE_SPLASH_GAP,
        }}>
        <Box style={{ flex: 1 }} />
        {imagePreviewUrl && (
          <Box
            style={{
              width: MOBILE_SPLASH_IMAGE_SIZE,
              height: MOBILE_SPLASH_IMAGE_SIZE,
              borderRadius: 8,
              overflow: 'hidden',
              flexShrink: 0,
            }}>
            <Image
              src={imagePreviewUrl}
              alt="Splash preview"
              w={MOBILE_SPLASH_IMAGE_SIZE}
              h={MOBILE_SPLASH_IMAGE_SIZE}
              fit="contain"
            />
          </Box>
        )}
        <Stack gap={2} align="center" w="100%" style={{ minWidth: 0, paddingInline: MOBILE_SPLASH_TEXT_PADDING_X }}>
          <Text
            ta="center"
            lineClamp={MOBILE_SPLASH_MAX_LINES}
            w="100%"
            style={{
              color: lightBg ? '#000' : '#FFF',
              fontSize: MOBILE_SPLASH_TITLE_FONT_SIZE,
              fontFamily: MOBILE_SPLASH_FONT_FAMILY,
              fontWeight: 600,
              letterSpacing: MOBILE_SPLASH_TITLE_LETTER_SPACING,
              textTransform: 'uppercase',
              lineHeight: MOBILE_SPLASH_LINE_HEIGHT,
            }}>
            {brandTitle || 'Channel Name'}
          </Text>
          {brandSubtitle ? (
            <Text
              ta="center"
              lineClamp={MOBILE_SPLASH_MAX_LINES}
              w="100%"
              style={{
                color: lightBg ? '#555' : '#DDD',
                fontSize: MOBILE_SPLASH_SUBTITLE_FONT_SIZE,
                fontFamily: MOBILE_SPLASH_FONT_FAMILY,
                fontWeight: 400,
                letterSpacing: MOBILE_SPLASH_SUBTITLE_LETTER_SPACING,
                textTransform: 'uppercase',
                lineHeight: MOBILE_SPLASH_LINE_HEIGHT,
              }}>
              {brandSubtitle}
            </Text>
          ) : null}
        </Stack>
        <Box style={{ flex: 1 }} />
        <Text
          fz={10}
          ta="center"
          style={{
            color: lightBg ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
            letterSpacing: 2,
            marginBottom: 16,
          }}>
          Loading &bull;&bull;&bull;
        </Text>
      </Box>
    </Box>
  )
}

function isLightColor(hex: string | undefined): boolean {
  if (!hex) return true
  const c = hex.replace('#', '')
  if (c.length < 6) return true
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // Relative luminance approximation
  return (r * 299 + g * 587 + b * 114) / 1000 > 128
}
